import React, { useState } from 'react';
import { cn } from '../lib/utils';

export function VisitDailyChart({ diario }) {
    if (!diario?.length) return null;
    const max = Math.max(1, ...diario.map((d) => d.sesiones));
    const total = diario.reduce((a, d) => a + d.sesiones, 0);
    const sinRegistro = diario.filter((d) => !d.registrado).length;
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="visit-daily-chart">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900">Sesiones por día · últimos 30 días</h4>
                <span className="text-xs text-slate-500">
                    Total: <strong className="font-mono text-slate-800" data-testid="visit-daily-total">{total.toLocaleString('es-CL')}</strong>
                </span>
            </div>
            <div className="mt-3 flex h-32 items-end gap-[3px]">
                {diario.map((d) => (
                    <div key={d.fecha} className="group relative flex h-full flex-1 items-end" title={`${d.label}: ${d.registrado ? `${d.sesiones} sesiones` : 'sin registro'}`}>
                        <div
                            className={cn(
                                'w-full rounded-t-sm transition-colors',
                                !d.registrado
                                    ? 'bg-slate-100 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,#cbd5e1_3px,#cbd5e1_4px)]'
                                    : d.sesiones === 0 ? 'bg-slate-200' : 'bg-sky-600 group-hover:bg-emerald-500'
                            )}
                            style={{ height: d.registrado ? `${Math.max(d.sesiones > 0 ? 6 : 2, (d.sesiones / max) * 100)}%` : '100%' }}
                            data-testid={`visit-daily-bar-${d.fecha}`}
                        />
                        <span className="pointer-events-none absolute -top-6 left-1/2 hidden -translate-x-1/2 rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-white group-hover:block">
                            {d.sesiones}
                        </span>
                    </div>
                ))}
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-slate-400">
                <span>{diario[0].label}</span>
                <span>{diario[Math.floor(diario.length / 2)].label}</span>
                <span>{diario[diario.length - 1].label}</span>
            </div>
            {sinRegistro > 0 && (
                <p className="mt-2 text-[11px] text-amber-700">Los {sinRegistro} días rayados son anteriores al inicio del registro (sin datos).</p>
            )}
        </div>
    );
}

export function VisitSourcesList({ fuentes }) {
    const [ventana, setVentana] = useState('30d');
    const rows = fuentes?.[ventana] || [];
    const total = rows.reduce((a, r) => a + r.sesiones, 0);
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="visit-sources-list">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900">Origen de las sesiones</h4>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs">
                    {['30d', '90d'].map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => setVentana(v)}
                            className={cn('rounded-md px-2.5 py-1 font-medium transition-colors', ventana === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100')}
                            data-testid={`visit-sources-toggle-${v}`}
                        >
                            {v === '30d' ? '30 días' : '90 días'}
                        </button>
                    ))}
                </div>
            </div>
            {rows.length === 0 ? (
                <p className="mt-3 text-xs text-slate-500" data-testid="visit-sources-empty">Sin sesiones en este período.</p>
            ) : (
                <ul className="mt-3 space-y-2">
                    {rows.map((r) => (
                        <li key={r.key} data-testid={`visit-source-${r.key}`}>
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-medium text-slate-800">{r.label}</span>
                                <span className="font-mono text-slate-600">{r.sesiones.toLocaleString('es-CL')} · {r.pct}%</span>
                            </div>
                            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                <div className="h-full rounded-full bg-gradient-to-r from-sky-600 to-emerald-500 transition-[width] duration-500" style={{ width: `${r.pct}%` }} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <p className="mt-3 text-[10px] text-slate-400">Fuente de llegada del primer pageview de cada sesión · {total.toLocaleString('es-CL')} sesiones</p>
        </div>
    );
}
